import React, { useEffect, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import formatDateWithTimezone from "../../../utils/dateUtils";
import { Modal } from "react-bootstrap";

const TransactionModal = ({ visible, onClose, transaction, timezone }) => {
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [selectedShipment, setSelectedShipment] = useState(null);
  
    const transactionGridRef = useRef();
    const shipmentGridRef = useRef();
    const shipmentItemsGridRef = useRef();
  
    const transactions = transaction?.transactions || [];
  
    useEffect(() => {
  
      transactions.forEach((t) => {
        Object.entries(t).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            console.log(`Array key found in transaction: ${key}`, value);
          }
        });
      });
  
      // Select first transaction with shipments if available, else first transaction
      const txWithShipments = transactions.find(
        (tx) => Array.isArray(tx.transaction_shipments) && tx.transaction_shipments.length > 0
      );
  
      if (txWithShipments) {
        setSelectedTransaction(txWithShipments);
      } else if (transactions.length > 0) {
        setSelectedTransaction(transactions[0]);
      } else {
        setSelectedTransaction(null);
      }
  
      setSelectedShipment(null);
  
      // Reset grid selections to avoid stale selections
      if (transactionGridRef.current?.api) transactionGridRef.current.api.deselectAll();
      if (shipmentGridRef.current?.api) shipmentGridRef.current.api.deselectAll();
      if (shipmentItemsGridRef.current?.api) shipmentItemsGridRef.current.api.deselectAll();
  
    }, [transaction]);
  
    if (!transaction) return null;
  
    // Transaction Columns
    const transactionColumns = [
      { headerName: "ID", field: "id", width: 160, resizable: true },
      { headerName: "Offer ID", field: "offer_id", width: 160, resizable: true },
      { headerName: "Created By", field: "created_by", width: 160, resizable: true },
      { headerName: "Offer Name", field: "offer_name", width: 160, resizable: true },
      {
        headerName: "Merchant Descriptor",
        valueGetter: (p) => p.data.merchant?.merchant_descriptor || "",
        width: 160,
        resizable: true,
      },
      {
        headerName: "Merchant Name",
        valueGetter: (p) => p.data.merchant?.merchant_name || "",
        width: 160,
        resizable: true,
      },
      { headerName: "Transaction Type ID", field: "transaction_type_id", width: 160, resizable: true },
      { headerName: "Transaction Total", field: "transaction_total", width: 160, resizable: true, valueFormatter: (p) => `$${p.value}` },
      { headerName: "Modified By", field: "modified_by", width: 160, resizable: true },
      { headerName: "Date Deposit", field: "date_deposit", valueGetter: (p) => formatDateWithTimezone(p.data.date_deposit, timezone), width: 220, resizable: true },
      { headerName: "Status", field: "status_type_id", width: 160, resizable: true },
      { headerName: "Payment Method", field: "payment_method_id", width: 160, resizable: true },
      { headerName: "Notes", field: "transaction_notes", width: 160, resizable: true },
      { headerName: "Declined", field: "transaction_declined", width: 160, resizable: true },
      { headerName: "Processor Response", field: "processor_response_text", width: 160, resizable: true },
      { headerName: "Gateway Response", field: "gateway_response_description", width: 160, resizable: true },
    ];
  
    return (
      <Modal show={visible} onHide={onClose} size="xl" centered dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Transactions for Order ID: {transaction.id}</Modal.Title>
        </Modal.Header>
  
        <Modal.Body>
          {/* Transaction Table */}
          <h5 className="mb-2">Transactions ({transactions.length})</h5>
          <div className="ag-theme-alpine" style={{ height: 300, width: "100%", marginBottom: 20 }}>
            <AgGridReact
              ref={transactionGridRef}
              rowData={transactions}
              columnDefs={transactionColumns}
              rowSelection="single"
              onSelectionChanged={() => {
                const selectedNodes = transactionGridRef.current.api.getSelectedNodes();
                if (selectedNodes.length > 0) {
                  console.log("✅ Selected Transaction:", selectedNodes[0].data);
                  setSelectedTransaction(selectedNodes[0].data);
                  setSelectedShipment(null);
                }
              }}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              suppressRowClickSelection={true}
            />
          </div>
  
          {/* Shipment Table */}
          {(selectedTransaction?.transaction_shipments?.length || 0) > 0 ? (
            <>
              <h6 className="mt-4">Shipments ({selectedTransaction.transaction_shipments.length})</h6>
              <div className="ag-theme-alpine" style={{ height: 200, width: "100%", marginBottom: 20 }}>
                <AgGridReact
                  ref={shipmentGridRef}
                  rowData={selectedTransaction.transaction_shipments}
                  columnDefs={[
                    { headerName: "Transaction ID", field: "transaction_charge_id", width: 160, resizable: true },
                    { headerName: "ID", field: "id", width: 160, resizable: true },
                    { headerName: "Shipping Profile", field: "shipping_profile_id", width: 200, resizable: true },
                    { headerName: "Shipping Tracking", field: "shipment_tracking_id", width: 200, resizable: true },
                    { headerName: "Shipping Status", field: "shipment_status_id", width: 200, resizable: true },
                    { headerName: "Carrier", field: "carrier_id", width: 160, resizable: true },
                  ]}
                  rowSelection="single"
                  onSelectionChanged={() => {
                    const selectedNodes = shipmentGridRef.current.api.getSelectedNodes();
                    if (selectedNodes.length > 0) {
                      console.log("📦 Selected Shipment:", selectedNodes[0].data);
                      setSelectedShipment(selectedNodes[0].data);
                    }
                  }}
                  defaultColDef={{ sortable: true, filter: true, resizable: true }}
                  suppressRowClickSelection={true}
                />
              </div>
            </>
          ) : (
            <p className="text-muted">No shipments available for selected transaction.</p>
          )}
  
          {/* Shipment Items Table */}
          {(selectedShipment?.shipment_items?.length || 0) > 0 ? (
            <>
              <h6 className="mt-4">Shipment Items ({selectedShipment.shipment_items.length})</h6>
              <div className="ag-theme-alpine" style={{ height: 200, width: "100%" }}>
                <AgGridReact
                  ref={shipmentItemsGridRef}
                  rowData={selectedShipment.shipment_items}
                  columnDefs={[
                    { headerName: "ID", field: "id", width: 160, resizable: true },
                    { headerName: "Item ID", field: "item_id", width: 160, resizable: true },
                    { headerName: "Quantity", field: "quantity", width: 160, resizable: true },
                    { headerName: "Item Name", field: "item_name", width: 160, resizable: true },
                    { headerName: "SKU", field: "shipment_item_sku", width: 160, resizable: true },
                    { headerName: "Status", field: "shipment_status_id", width: 160, resizable: true },
                  ]}
                  defaultColDef={{ sortable: true, filter: true, resizable: true }}
                />
              </div>
            </>
          ) : (
            selectedShipment && <p className="text-muted">No shipment items available for selected shipment.</p>
          )}
  
        </Modal.Body>
      </Modal>
    );
  };
  export default TransactionModal;